/**
 * Cross-module vocabulary for ERP registry environments.
 *
 * The `environment` attribute is a nullable, enum-backed string on the origami
 * API (`production | preprod | recette | staging | test | development`). Both
 * the admin UI (registry form, list badge, detail page) and the claimflow
 * non-production banner read their type and labels from here, so a label
 * change or a new environment value stays a one-line change in a single file.
 *
 * Admin-only presentation (select options, badge variants) stays in
 * `src/modules/admin/configs/erpEnvironment.ts`, which re-exports the labels.
 */

/**
 * The environment an ERP registry points to.
 *
 * Mirrors the `ErpEnvironment` enum exposed by the origami API. `null` (no
 * environment set) is modelled at the call site as `ErpEnvironment | null`.
 */
export type ErpEnvironment =
  | 'production'
  | 'preprod'
  | 'recette'
  | 'staging'
  | 'test'
  | 'development';

/** Human-readable label for every environment value. */
export const ERP_ENVIRONMENT_LABELS: Record<ErpEnvironment, string> = {
  production: 'Production',
  preprod: 'Préprod',
  recette: 'Recette',
  staging: 'Staging',
  test: 'Test',
  development: 'Development',
};

/**
 * Every string rendered by `ErpEnvironmentBanner`, in French like the rest of
 * claimflow. Kept as config (not inline JSX) so the copy can be reviewed and
 * overridden in one place.
 */
export const ERP_ENVIRONMENT_BANNER_COPY = {
  /** Accessible name of the banner region. */
  ariaLabel: 'Environnement ERP non production',

  /** Title when every listed registry shares one (tagged) environment. */
  titleSingle: (label: string): string => `Environnement ${label}`,

  /** Title when the listed registries span several environments. */
  titleMixed: 'Environnement hors production',

  /** Title when every listed registry has no environment set. */
  titleUnset: 'Environnement non renseigné',

  /** Label standing in for a `null` environment on a registry line. */
  unsetLabel: 'Non renseigné',

  /**
   * One line per listed registry, ending with the ERP host it talks to.
   *
   * The base URL is what staff actually need when the banner fires: the
   * environment tag says how the registry is labelled, the base URL says
   * which ERP instance the UI is really hitting.
   */
  registryLine: (
    name: string,
    slug: string,
    label: string,
    baseUrl: string,
  ): string => `${name} (${slug}) — ${label} — ${baseUrl}`,

  /** Joins the banner title to the registry part of the single line. */
  titleSeparator: ' - ',

  /** Joins registry lines when several non-production registries are listed. */
  registrySeparator: ' · ',

  /**
   * The banner's whole text, as one line.
   *
   * The banner is a single compact bar, so the title and every registry line
   * are concatenated here rather than stacked in JSX: `<title> - <registry
   * line>[ · <registry line>…]`. The line wraps naturally when the viewport is
   * too narrow; nothing is truncated.
   */
  compactLine: (title: string, registryLines: readonly string[]): string =>
    `${title}${ERP_ENVIRONMENT_BANNER_COPY.titleSeparator}${registryLines.join(
      ERP_ENVIRONMENT_BANNER_COPY.registrySeparator,
    )}`,
} as const;

/**
 * Label for an environment value, falling back to the "not set" label.
 *
 * @param environment - The registry environment, or `null` when untagged.
 */
export function getErpEnvironmentLabel(
  environment: ErpEnvironment | null,
): string {
  return environment === null
    ? ERP_ENVIRONMENT_BANNER_COPY.unsetLabel
    : ERP_ENVIRONMENT_LABELS[environment];
}
